import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Copy, ArrowLeft, Loader2, LogOut, Check } from "lucide-react";
import { VoiceSamples } from "../components/scout/VoiceSamples";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch {
      /* ignore */
    }
  }
};

const CopyBtn = ({ text, label = "Copy", testid }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={async () => { await copyText(text); setDone(true); setTimeout(() => setDone(false), 1800); }}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full border border-[#AEE6D5]/30 px-3 py-1.5 text-xs font-bold text-[#AEE6D5] transition-colors hover:bg-[#AEE6D5]/10"
    >
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {done ? "Copied!" : label}
    </button>
  );
};

const Block = ({ title, children, copy, testid }) => (
  <div className="rounded-2xl border border-[#AEE6D5]/12 bg-[#0B3B3C]/50 p-5" data-testid={testid}>
    <div className="mb-3 flex items-center justify-between gap-3">
      <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-[#AEE6D5]">{title}</p>
      {copy != null && <CopyBtn text={copy} testid={`${testid}-copy`} />}
    </div>
    {children}
  </div>
);

export default function Scout() {
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState("");
  const [questions, setQuestions] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const draft = async () => {
    if (opportunity.trim().length < 10 || busy) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await fetch(`${API}/scout/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ opportunity, questions }),
      });
      if (!res.ok) throw new Error("failed");
      setResult(await res.json());
    } catch {
      setError("Couldn't draft the application — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try { await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }); }
    finally { navigate("/login", { replace: true }); }
  };

  const everything = result
    ? [
        `COVER NOTE\n${result.cover_note}`,
        result.talk_titles?.length ? `\nTALK TITLES\n- ${result.talk_titles.join("\n- ")}` : "",
        result.speaker_bio ? `\nSPEAKER BIO\n${result.speaker_bio}` : "",
        result.answers?.length ? `\nANSWERS\n${result.answers.map((a) => `Q: ${a.question}\nA: ${a.answer}`).join("\n\n")}` : "",
      ].filter(Boolean).join("\n")
    : "";

  return (
    <div className="min-h-screen bg-[#0A2E2C] text-[#F8FAFC]" data-testid="scout">
      <header className="sticky top-0 z-20 border-b border-[#AEE6D5]/12 bg-[#0A2E2C]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-[#D4AF37]">Casey&#39;s Cadence</p>
            <h1 className="font-serif text-2xl font-light">Booking Scout</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" data-testid="scout-dashboard-link" className="inline-flex items-center gap-2 rounded-full border border-[#AEE6D5]/30 px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-[#AEE6D5] transition-colors hover:bg-[#AEE6D5]/10">
              <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
            </Link>
            <button onClick={logout} data-testid="scout-logout" className="inline-flex items-center gap-2 rounded-full border border-[#AEE6D5]/30 px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-[#AEE6D5] transition-colors hover:bg-[#AEE6D5]/10">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-8 sm:px-8">
        <p className="max-w-2xl text-sm leading-relaxed text-[#F8FAFC]/70">
          Paste a speaking opportunity below (event, organization, description, link) and any application questions.
          The Scout will draft a complete application — cover note, tailored talk titles, bio, and answers — in your voice.
        </p>

        <div className="mt-6">
          <VoiceSamples />
        </div>

        <div className="mt-6 space-y-4">
          <textarea
            value={opportunity}
            onChange={(e) => setOpportunity(e.target.value)}
            rows={5}
            placeholder="Paste the opportunity: event name, organization, audience, theme, location, link…"
            data-testid="scout-opportunity"
            className="w-full resize-y rounded-xl border border-[#AEE6D5]/25 bg-[#0B3B3C]/60 px-4 py-3.5 text-sm text-[#F8FAFC] placeholder-[#F8FAFC]/40 outline-none transition-colors focus:border-[#D4AF37]"
          />
          <textarea
            value={questions}
            onChange={(e) => setQuestions(e.target.value)}
            rows={4}
            placeholder="Optional — paste the application questions, one per line"
            data-testid="scout-questions"
            className="w-full resize-y rounded-xl border border-[#AEE6D5]/25 bg-[#0B3B3C]/60 px-4 py-3.5 text-sm text-[#F8FAFC] placeholder-[#F8FAFC]/40 outline-none transition-colors focus:border-[#D4AF37]"
          />
          {error && <p className="text-sm text-red-300" data-testid="scout-error">{error}</p>}
          <button
            onClick={draft}
            disabled={busy || opportunity.trim().length < 10}
            data-testid="scout-draft-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#D4AF37] px-8 py-4 text-sm font-bold text-[#0A2E2C] transition-colors hover:bg-[#B99628] disabled:opacity-50 sm:w-auto"
          >
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Drafting in your voice…</> : <><Sparkles className="h-4 w-4" /> Draft in my voice</>}
          </button>
        </div>

        {result && (
          <div className="mt-10 space-y-5" data-testid="scout-result">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl text-[#F8FAFC]">Your application draft</h2>
              <CopyBtn text={everything} label="Copy everything" testid="scout-copy-all" />
            </div>

            <Block title="Cover note" copy={result.cover_note} testid="scout-cover">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#F8FAFC]/85">{result.cover_note}</p>
            </Block>

            {result.talk_titles?.length > 0 && (
              <Block title="Suggested talk titles" copy={result.talk_titles.join("\n")} testid="scout-titles">
                <ul className="space-y-2">
                  {result.talk_titles.map((t, i) => (
                    <li key={i} className="flex gap-2 text-sm text-[#F8FAFC]/85"><span className="text-[#D4AF37]">—</span>{t}</li>
                  ))}
                </ul>
              </Block>
            )}

            {result.speaker_bio && (
              <Block title="Speaker bio" copy={result.speaker_bio} testid="scout-bio">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#F8FAFC]/85">{result.speaker_bio}</p>
              </Block>
            )}

            {result.answers?.length > 0 && (
              <Block title="Application answers" copy={result.answers.map((a) => `Q: ${a.question}\nA: ${a.answer}`).join("\n\n")} testid="scout-answers">
                <div className="space-y-4">
                  {result.answers.map((a, i) => (
                    <div key={i}>
                      <p className="text-xs font-bold text-[#D4AF37]">{a.question}</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#F8FAFC]/85">{a.answer}</p>
                    </div>
                  ))}
                </div>
              </Block>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
